import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import HtmlIcon from '@mui/icons-material/Html';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

interface SlideExportDialogProps {
  open: boolean;
  onClose: () => void;
  finalHtml: string;
  lessonTitle?: string;
}

// ── Pure helpers (no React) ───────────────────────────────────────────────────

// DOMParser cannot traverse into SVG <foreignObject> sub-documents, so all
// DOM-based approaches (querySelectorAll, getElementById) miss the MARP slide
// content.  Every helper below works directly on the raw HTML string instead.

/**
 * Each MARP slide contains exactly one <svg data-marpit-svg> element.
 * Counting that attribute is the same approach used by MARP's own scripts.
 */
function countSections(html: string): number {
  return (html.match(/data-marpit-svg/g) ?? []).length;
}

/**
 * Collects the text content of every <style> block in the HTML string.
 * CSS cannot create iframes or trigger file:// security errors — only
 * JavaScript can — so it is safe to include all styles (Bespoke.js layout
 * styles + MARP theme styles) without including any <script> tags.
 */
function getAllCSS(html: string): string {
  return Array.from(html.matchAll(/<style[^>]*>([\s\S]*?)<\/style>/gi))
    .map((m) => m[1])
    .filter(Boolean)
    .join('\n');
}

/**
 * Builds a self-contained HTML document with only the selected MARP slides.
 *
 * Extracts the <section> elements (one per slide, inside each SVG
 * foreignObject) and all <style> blocks, then assembles a minimal HTML file.
 * No <script> tags are included — this avoids the Bespoke.js presenter-view
 * iframe that causes Chrome's "Unsafe attempt to load URL file://" error
 * when the file is opened locally.  Each section gets its own print page.
 */
function buildPartialHtml(finalHtml: string, slideNumbers: number[]): string {
  const sectionRe = /<section[^>]*>[\s\S]*?<\/section>/gi;
  const allSections = [...finalHtml.matchAll(sectionRe)].map((m) => m[0]);

  if (allSections.length === 0) return finalHtml;

  const selected = slideNumbers
    .filter((n) => n >= 1 && n <= allSections.length)
    .map((n) => allSections[n - 1]);

  if (selected.length === 0) return finalHtml;

  const allCSS = getAllCSS(finalHtml);

  return [
    '<!DOCTYPE html>',
    '<html lang="en">',
    '<head>',
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    '<style>',
    // CSS-variable fallbacks for values normally injected by MARP's SVG context.
    ':root{--marp-root-font-size:28px;--marp-slide-width:1280px;--marp-slide-height:720px;}',
    allCSS,
    'body{margin:0;padding:0;}',
    'section{min-height:100vh;width:100%;box-sizing:border-box;}',
    '@media print{section{page-break-after:always;break-after:page;}}',
    '</style>',
    '</head>',
    '<body>',
    ...selected,
    '</body>',
    '</html>',
  ].join('\n');
}

/** Injects window.print() trigger so the browser opens the print dialog on load. */
function injectPrintTrigger(html: string): string {
  const script =
    '<script>(function(){' +
    "window.addEventListener('load',function(){setTimeout(function(){window.print();},400);});" +
    '}());</script>';
  return html.includes('</body>') ? html.replace('</body>', script + '</body>') : html + script;
}

function downloadHtml(html: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function openForPrint(html: string): void {
  const url = URL.createObjectURL(new Blob([injectPrintTrigger(html)], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function safeFilename(title: string): string {
  return (
    title
      .replace(/[^a-z0-9_-]/gi, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'slides'
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export const SlideExportDialog: React.FC<SlideExportDialogProps> = ({
  open,
  onClose,
  finalHtml,
  lessonTitle = 'lesson',
}) => {
  const totalSlides = useMemo(() => countSections(finalHtml), [finalHtml]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const toggleSlide = (n: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });

  const selectAll = () =>
    setSelected(new Set(Array.from({ length: totalSlides }, (_, i) => i + 1)));
  const deselectAll = () => setSelected(new Set());

  const base = safeFilename(lessonTitle);

  const handleDownloadAll = () => {
    downloadHtml(finalHtml, `${base}.html`);
    setSnackbar({ open: true, message: 'HTML download started.', severity: 'success' });
  };

  const handleExportPdf = () => {
    openForPrint(finalHtml);
    setSnackbar({
      open: true,
      message: 'Print dialog will open in the new tab — choose "Save as PDF".',
      severity: 'info',
    });
  };

  const handleDownloadSelected = () => {
    const slideNumbers = Array.from(selected).sort((a, b) => a - b);
    const partial = buildPartialHtml(finalHtml, slideNumbers);
    downloadHtml(partial, `${base}-slides-${slideNumbers.join('-')}.html`);
    setSnackbar({ open: true, message: `${slideNumbers.length} slide(s) downloaded.`, severity: 'success' });
  };

  const selectedCount = selected.size;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Export Slides</DialogTitle>

        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* ── PDF ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              PDF
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Opens the full deck in a print dialog — select pages there.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              disabled={totalSlides === 0}
              onClick={handleExportPdf}
            >
              Export as PDF
            </Button>
          </Box>

          <Divider />

          {/* ── HTML ── */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              HTML
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<HtmlIcon />}
              disabled={totalSlides === 0}
              onClick={handleDownloadAll}
              sx={{ mb: 2 }}
            >
              Download All ({totalSlides} slides)
            </Button>

            {/* Slide picker */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Or select specific slides:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button size="small" variant="text" onClick={selectAll}>All</Button>
              <Button size="small" variant="text" onClick={deselectAll} disabled={selectedCount === 0}>
                None
              </Button>
            </Box>

            {totalSlides === 0 ? (
              <Alert severity="warning">No slides found in the assembled HTML.</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Array.from({ length: totalSlides }, (_, i) => i + 1).map((n) => {
                  const isSelected = selected.has(n);
                  return (
                    <Tooltip key={n} title={`Slide ${n}`}>
                      <Box
                        onClick={() => toggleSlide(n)}
                        sx={{
                          width: 44,
                          height: 44,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          border: '2px solid',
                          borderColor: isSelected ? 'primary.main' : 'divider',
                          bgcolor: isSelected ? 'primary.50' : 'background.paper',
                          color: isSelected ? 'primary.main' : 'text.secondary',
                          fontWeight: isSelected ? 700 : 400,
                          fontSize: 13,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                          userSelect: 'none',
                          '&:hover': { borderColor: 'primary.light', bgcolor: 'primary.50' },
                        }}
                      >
                        {n}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}

            {selectedCount > 0 && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<HtmlIcon />}
                  onClick={handleDownloadSelected}
                >
                  Download {selectedCount} Selected Slide{selectedCount !== 1 ? 's' : ''} as HTML
                </Button>
              </Box>
            )}
          </Box>

        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
